/**
 * Browser Compatibility Test Utility
 * Tests color rendering and CSS feature support across different browsers
 */

class BrowserCompatibilityTest {
  constructor() {
    this.testResults = {
      cssCustomProperties: false,
      gradientSupport: false,
      backdropFilter: false,
      colorGamut: 'srgb',
      prefersReducedMotion: false,
      prefersHighContrast: false,
      browserInfo: this.getBrowserInfo()
    };
    
    this.runAllTests();
  }

  /**
   * Get browser information
   */
  getBrowserInfo() {
    const userAgent = navigator.userAgent;
    const isChrome = /Chrome/.test(userAgent) && /Google Inc/.test(navigator.vendor);
    const isFirefox = /Firefox/.test(userAgent);
    const isSafari = /Safari/.test(userAgent) && /Apple Computer/.test(navigator.vendor);
    const isEdge = /Edg/.test(userAgent);
    const isIE = /MSIE|Trident/.test(userAgent);
    
    const browserInfo = {
      isChrome,
      isFirefox,
      isSafari,
      isEdge,
      isIE,
      userAgent,
      version: this.getBrowserVersion(userAgent, { isChrome, isFirefox, isSafari, isEdge })
    };
    
    return browserInfo;
  }

  /**
   * Extract browser version from user agent
   */
  getBrowserVersion(userAgent, browserInfo = null) {
    let version = 'Unknown';
    
    // Use passed browserInfo or fallback to this.testResults.browserInfo
    const info = browserInfo || this.testResults?.browserInfo;
    
    if (info?.isChrome) {
      const match = userAgent.match(/Chrome\/(\d+)/);
      version = match ? match[1] : 'Unknown';
    } else if (info?.isFirefox) {
      const match = userAgent.match(/Firefox\/(\d+)/);
      version = match ? match[1] : 'Unknown';
    } else if (info?.isSafari) {
      const match = userAgent.match(/Version\/(\d+)/);
      version = match ? match[1] : 'Unknown';
    } else if (info?.isEdge) {
      const match = userAgent.match(/Edg\/(\d+)/);
      version = match ? match[1] : 'Unknown';
    }
    
    return version;
  }

  /**
   * Run all compatibility tests
   */
  runAllTests() {
    this.testCSSCustomProperties();
    this.testGradientSupport();
    this.testBackdropFilter();
    this.testColorGamut();
    this.testUserPreferences();
    this.testColorContrast();
  }

  /**
   * Test CSS Custom Properties support
   */
  testCSSCustomProperties() {
    try {
      const testElement = document.createElement('div');
      testElement.style.setProperty('--test-var', 'test');
      const computedStyle = getComputedStyle(testElement);
      
      // Check if CSS custom properties are supported
      this.testResults.cssCustomProperties = CSS.supports('--test', 'test') || 
        computedStyle.getPropertyValue('--test-var') !== '';
        
      // Clean up
      testElement.remove();
    } catch (error) {
      console.warn('CSS Custom Properties test failed:', error);
      this.testResults.cssCustomProperties = false;
    }
  }

  /**
   * Test gradient support
   */
  testGradientSupport() {
    try {
      const testElement = document.createElement('div');
      const gradientValues = [
        'linear-gradient(to right, red, blue)',
        '-webkit-linear-gradient(left, red, blue)',
        '-moz-linear-gradient(left, red, blue)',
        '-o-linear-gradient(left, red, blue)'
      ];

      for (const gradient of gradientValues) {
        testElement.style.background = gradient;
        if (testElement.style.background.includes('gradient')) {
          this.testResults.gradientSupport = true;
          break;
        }
      }

      // Clean up
      testElement.remove();
    } catch (error) {
      console.warn('Gradient support test failed:', error);
      this.testResults.gradientSupport = false;
    }
  }

  /**
   * Test backdrop-filter support
   */
  testBackdropFilter() {
    try {
      this.testResults.backdropFilter = CSS.supports('backdrop-filter', 'blur(10px)') ||
        CSS.supports('-webkit-backdrop-filter', 'blur(10px)');
    } catch (error) {
      console.warn('Backdrop filter test failed:', error);
      this.testResults.backdropFilter = false;
    }
  }

  /**
   * Test color gamut support
   */
  testColorGamut() {
    try {
      if (window.matchMedia) {
        if (window.matchMedia('(color-gamut: p3)').matches) {
          this.testResults.colorGamut = 'p3';
        } else if (window.matchMedia('(color-gamut: srgb)').matches) {
          this.testResults.colorGamut = 'srgb';
        } else if (window.matchMedia('(color-gamut: rec2020)').matches) {
          this.testResults.colorGamut = 'rec2020';
        }
      }
    } catch (error) {
      console.warn('Color gamut test failed:', error);
      this.testResults.colorGamut = 'srgb';
    }
  }

  /**
   * Test user preferences
   */
  testUserPreferences() {
    try {
      if (window.matchMedia) {
        this.testResults.prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        this.testResults.prefersHighContrast = window.matchMedia('(prefers-contrast: high)').matches;
      }
    } catch (error) {
      console.warn('User preferences test failed:', error);
    }
  }

  /**
   * Test color contrast calculations
   */
  testColorContrast() {
    try {
      // Test if the browser can calculate color contrast
      const testColors = [
        { bg: '#000000', fg: '#ffffff', expectedRatio: 21 },
        { bg: '#2563EB', fg: '#ffffff', expectedRatio: 4.5 },
        { bg: '#F8FAFC', fg: '#0F172A', expectedRatio: 15 }
      ];

      this.testResults.contrastTests = testColors.map(test => ({
        ...test,
        calculatedRatio: this.calculateContrastRatio(test.bg, test.fg),
        passes: this.calculateContrastRatio(test.bg, test.fg) >= 4.5
      }));
    } catch (error) {
      console.warn('Color contrast test failed:', error);
      this.testResults.contrastTests = [];
    }
  }

  /**
   * Calculate contrast ratio between two colors
   */
  calculateContrastRatio(color1, color2) {
    try {
      const luminance1 = this.getLuminance(color1);
      const luminance2 = this.getLuminance(color2);
      
      const lighter = Math.max(luminance1, luminance2);
      const darker = Math.min(luminance1, luminance2);
      
      return (lighter + 0.05) / (darker + 0.05);
    } catch (error) {
      console.warn('Contrast ratio calculation failed:', error);
      return 1;
    }
  }

  /**
   * Get relative luminance of a color
   */
  getLuminance(color) {
    try {
      const rgb = this.hexToRgb(color);
      if (!rgb) return 0;

      const [r, g, b] = [rgb.r, rgb.g, rgb.b].map(c => {
        c = c / 255;
        return c <= 0.03928 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4);
      });

      return 0.2126 * r + 0.7152 * g + 0.0722 * b;
    } catch (error) {
      console.warn('Luminance calculation failed:', error);
      return 0;
    }
  }

  /**
   * Convert hex color to RGB
   */
  hexToRgb(hex) {
    try {
      const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
      return result ? {
        r: parseInt(result[1], 16),
        g: parseInt(result[2], 16),
        b: parseInt(result[3], 16)
      } : null;
    } catch (error) {
      console.warn('Hex to RGB conversion failed:', error);
      return null;
    }
  }

  /**
   * Get fallback colors for unsupported browsers
   */
  getFallbackColors() {
    const fallbacks = {
      primary: '#2563EB',
      secondary: '#0891B2',
      accent: '#F59E0B',
      success: '#10B981',
      warning: '#F59E0B',
      error: '#EF4444',
      info: '#3B82F6',
      background: '#0F172A',
      text: '#F8FAFC'
    };

    // Adjust fallbacks based on browser capabilities
    if (!this.testResults.cssCustomProperties) {
      // For browsers without CSS custom properties, use simpler colors
      return {
        ...fallbacks,
        gradients: false,
        animations: false
      };
    }

    if (!this.testResults.gradientSupport) {
      return {
        ...fallbacks,
        gradients: false
      };
    }

    return fallbacks;
  }

  /**
   * Apply browser-specific optimizations
   */
  applyOptimizations() {
    const optimizations = {
      disableAnimations: this.testResults.prefersReducedMotion,
      useHighContrast: this.testResults.prefersHighContrast,
      useFallbackColors: !this.testResults.cssCustomProperties,
      disableGradients: !this.testResults.gradientSupport,
      disableBackdropFilter: !this.testResults.backdropFilter
    };

    // Apply optimizations to document
    if (optimizations.disableAnimations) {
      document.documentElement.classList.add('reduce-motion');
    }

    if (optimizations.useHighContrast) {
      document.documentElement.classList.add('high-contrast');
    }

    if (optimizations.useFallbackColors) {
      document.documentElement.classList.add('fallback-colors');
    }

    if (optimizations.disableGradients) {
      document.documentElement.classList.add('no-gradients');
    }

    if (optimizations.disableBackdropFilter) {
      document.documentElement.classList.add('no-backdrop-filter');
    }

    return optimizations;
  }

  /**
   * Generate compatibility report
   */
  generateReport() {
    const report = {
      timestamp: new Date().toISOString(),
      browser: this.testResults.browserInfo,
      features: {
        cssCustomProperties: this.testResults.cssCustomProperties,
        gradientSupport: this.testResults.gradientSupport,
        backdropFilter: this.testResults.backdropFilter,
        colorGamut: this.testResults.colorGamut
      },
      userPreferences: {
        prefersReducedMotion: this.testResults.prefersReducedMotion,
        prefersHighContrast: this.testResults.prefersHighContrast
      },
      colorTests: this.testResults.contrastTests || [],
      recommendations: this.getRecommendations()
    };

    return report;
  }

  /**
   * Get recommendations based on test results
   */
  getRecommendations() {
    const recommendations = [];

    if (!this.testResults.cssCustomProperties) {
      recommendations.push({
        type: 'fallback',
        message: 'Use fallback colors for browsers without CSS custom property support',
        action: 'Apply .fallback-colors class to document'
      });
    }

    if (!this.testResults.gradientSupport) {
      recommendations.push({
        type: 'gradient',
        message: 'Use solid colors instead of gradients',
        action: 'Apply .no-gradients class to document'
      });
    }

    if (!this.testResults.backdropFilter) {
      recommendations.push({
        type: 'backdrop',
        message: 'Use alternative styling for glass effects',
        action: 'Apply .no-backdrop-filter class to document'
      });
    }

    if (this.testResults.prefersReducedMotion) {
      recommendations.push({
        type: 'motion',
        message: 'Disable animations and transitions',
        action: 'Apply .reduce-motion class to document'
      });
    }

    if (this.testResults.prefersHighContrast) {
      recommendations.push({
        type: 'contrast',
        message: 'Use high contrast color scheme',
        action: 'Apply .high-contrast class to document'
      });
    }

    return recommendations;
  }

  /**
   * Test color performance
   */
  testColorPerformance() {
    const performanceTests = [];

    // Test gradient rendering performance
    const gradientTest = this.measurePerformance(() => {
      const element = document.createElement('div');
      element.style.background = 'linear-gradient(135deg, #2563EB 0%, #7C3AED 50%, #4F46E5 100%)';
      element.style.width = '100px';
      element.style.height = '100px';
      document.body.appendChild(element);
      
      // Force reflow
      // eslint-disable-next-line no-unused-expressions
      element.offsetHeight;
      
      document.body.removeChild(element);
    });

    performanceTests.push({
      name: 'Gradient Rendering',
      duration: gradientTest,
      acceptable: gradientTest < 5 // 5ms threshold
    });

    // Test color transition performance
    const transitionTest = this.measurePerformance(() => {
      const element = document.createElement('div');
      element.style.transition = 'background-color 0.3s ease';
      element.style.backgroundColor = '#2563EB';
      document.body.appendChild(element);
      
      // Trigger transition
      element.style.backgroundColor = '#7C3AED';
      
      // Force reflow
      // eslint-disable-next-line no-unused-expressions
      element.offsetHeight;
      
      document.body.removeChild(element);
    });

    performanceTests.push({
      name: 'Color Transition',
      duration: transitionTest,
      acceptable: transitionTest < 2 // 2ms threshold
    });

    return performanceTests;
  }

  /**
   * Measure performance of a function
   */
  measurePerformance(fn) {
    const start = performance.now();
    fn();
    const end = performance.now();
    return end - start;
  }

  /**
   * Get all test results
   */
  getResults() {
    return {
      ...this.testResults,
      report: this.generateReport(),
      performance: this.testColorPerformance()
    };
  }
}

// Export for use in other modules
export default BrowserCompatibilityTest;

// Auto-run tests when module is loaded
export const browserCompatibility = new BrowserCompatibilityTest();

// Apply optimizations automatically
browserCompatibility.applyOptimizations();

// Log results in development
if (process.env.NODE_ENV === 'development') {
  console.log('Browser Compatibility Test Results:', browserCompatibility.getResults());
}